import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { PersonStatus } from 'src/app/people/models/personStatus';
import { SeatStatus } from 'src/app/shared/models/team/seatStatus';
import { ICustomView, CustomView } from '../components/odata-filter/models/custom-view';
import { DefaultView } from '../components/odata-filter/models/default-view';
import { FilterOperatorType } from '../components/odata-filter/models/filter-operator-type';
import { ODataFilterValues } from '../components/odata-filter/models/odata-filter';
import { OPERATORS } from '../components/odata-filter/models/operators';
import { SavedFilter } from '../components/odata-filter/models/saved-filter';
import { SelectedFilter } from '../components/odata-filter/models/selected-filter';
import {
  FORM_SYSTEM_FILTER_MODELS,
  PEOPLE_DEFAULT_QUICK_FILTERS,
  PEOPLE_FILTER_MODELS,
  TEAM_ROSTERS_REPORT_FILTER_MODELS,
  TEAM_SURVEY_SYSTEM_FILTER_MODELS,
  TEAM_SYSTEM_FILTER_MODELS
} from '../components/odata-filter/models/system-filter-models';
import { ViewContext } from '../components/odata-filter/models/view-context';
import { FormStatus } from '../models/application/formStatus';
import { AttributeDefinition } from '../models/attributes/attributeDefinition';
import { SurveyStatus } from '../models/survey/surveyStatus';
import { ApiVersions } from './api-version';
import { AttributeService } from './attribute.service';
import { BaseService } from './base.service';
import { CustomViewDisplayLayout } from '../components/odata-filter/models/custom-view-display-layout';
import { filterQuickFilterNamesBySystemFieldVisibility } from '../utils/system-field-visibility.util';

@Injectable({
  providedIn: 'root'
})
export class CustomViewService extends BaseService {
  private baseUrl = 'custom-views';

  private _customViews: CustomView[] = [];
  public readonly customViews = new BehaviorSubject<CustomView[]>(null);

  public readonly customViewCreated = new Subject<CustomView>();

  public readonly customViewUpdated = new Subject<CustomView>();

  public readonly customViewDeleted = new Subject<CustomView>();
  public readonly resetViewRequested = new Subject<void>();

  constructor(private attributeService: AttributeService) {
    super();

    this.attributeService.attributeDeleted.subscribe((att: AttributeDefinition) => {
      this._customViews.forEach((customView) => {
        let index = customView.columns.findIndex(
          (c) => c.isCustomField && c.customFieldId == att.id
        );
        if (index != -1) {
          customView.columns.splice(index, 1);
        }
      });
      this.customViews.next(this._customViews);
    });
  }
  public restViewCached() {
    this.resetViewRequested.next();
  }
  public findView(viewId: string, viewContext: ViewContext): CustomView {
    return this._customViews.find((x) => x.id == viewId && x.viewContext == viewContext);
  }

  public getContextViews(
    views: CustomView[],
    viewContext: ViewContext,
    viewContextId?: string
  ): CustomView[] {
    return views.filter(
      (x) =>
        x.viewContext == viewContext &&
        !x.isTemporary &&
        (x.isSystemView || x.viewContextId == viewContextId)
    );
  }

  /**
   * A pinned user view wins over a pinned system view. Falls back to the first system view
   * when nothing is pinned or the tenant's plan does not allow saved views.
   */
  public getDefaultView(contextViews: CustomView[]): CustomView {
    const pinnedView =
      contextViews.find((x) => x.isDefault && !x.isSystemView) ??
      contextViews.find((x) => x.isDefault && x.isSystemView);
    if (
      !pinnedView ||
      (!this.appConfigService.tenant.plan.canSaveViews && !pinnedView.isSystemView)
    ) {
      return contextViews.find((x) => x.isSystemView);
    }
    return pinnedView;
  }

  public getCustomViews() {
    this.get<{ defaultViews: DefaultView[]; customViews: CustomView[] }>(
      this.baseUrl,
      true,
      ApiVersions.unversioned
    )
      .pipe(map((data) => this.convertToCustomViews(data)))
      .subscribe((result) => {
        this._customViews = result;
        this.customViews.next(this._customViews);
      });
  }

  public create(customView: CustomView) {
    this.post(this.baseUrl, customView, null, ApiVersions.unversioned).subscribe(
      (result: ICustomView) => {
        if (!result) {
          this.customViewCreated.next(null);
          return;
        }

        this.openSnackBar(`Successfully created a new view`, true);
        let newView = new CustomView(result);
        this._customViews.push(newView);
        this.customViewCreated.next(newView);
      }
    );
  }

  public update(customView: CustomView) {
    this.patch(`${this.baseUrl}/${customView.id}`, customView, ApiVersions.unversioned).subscribe(
      (result: ICustomView) => {
        if (!result) {
          this.customViewUpdated.next(null);
          return;
        }
        this.openSnackBar(`Successfully updated the selected view`, true);
        let updatedView = new CustomView(result);
        updatedView.isDefault = customView.isDefault;
        this.updateCachedViews(updatedView);
      }
    );
  }

  public deleteView(customView: CustomView) {
    this.delete(`${this.baseUrl}/${customView.id}`, null, ApiVersions.unversioned).subscribe(() => {
      this.deleteFromCachedViews(customView);
      this.openSnackBar(`Successfully removed the view`, true);
    });
  }

  private deleteFromCachedViews(customView: CustomView) {
    let index = this._customViews.findIndex((x) => x.id == customView.id);

    if (index != -1) {
      this._customViews.splice(index, 1);
    }
    this.customViewDeleted.next(customView);
  }

  private updateCachedViews(customView: CustomView) {
    let index = this._customViews.findIndex((x) => x.id == customView.id);

    if (index != -1) {
      this._customViews[index] = customView;
    }
    this.customViewUpdated.next(customView);
  }

  clearDefaultView(viewContext: ViewContext) {
    this.delete(
      `${this.baseUrl}/default-view?ViewContext=${viewContext}`,
      null,
      ApiVersions.unversioned
    ).subscribe();
  }

  setDefault(view: CustomView) {
    var defaultView: DefaultView = {
      defaultViewId: view.id,
      viewContext: view.viewContext
    };
    let body = JSON.stringify(defaultView);
    return this.put(`${this.baseUrl}/default-view`, body, null, ApiVersions.unversioned).pipe(
      map((data) => {
        if (data && data.success) {
          this._customViews
            .filter((x) => x.viewContext == view.viewContext && x.isDefault)
            .forEach((x) => {
              x.isDefault = false;
            });

          let defaultViewIndex = this._customViews.findIndex(
            (x) => x.viewContext == view.viewContext && x.id == view.id
          );
          if (defaultViewIndex != -1) {
            this._customViews[defaultViewIndex].isDefault = true;
          }
          return true;
        }
        if (data && !data.success && data.errorMessages && data.errorMessages.length) {
          this.openSnackBar(data.errorMessages[0]);
        }
        return false;
      })
    );
  }

  public addToViews(view: CustomView) {
    if (
      this._customViews.findIndex((x) => x.id == view.id && x.viewContext == view.viewContext) == -1
    ) {
      this._customViews.push(view);
      this.customViews.next(this._customViews);
    }
  }

  public selectView(view: CustomView) {
    this._customViews
      .filter((x) => x.viewContext == view.viewContext)
      .forEach((item) => {
        item.isModified = false;
        if (item.id == view.id) {
          item.isSelected = true;
        } else {
          item.isSelected = false;
        }
      });
  }

  public reGenerateSystemViews(context: ViewContext) {
    this.restViewCached();
    this._customViews = this._customViews.filter((x) => x.viewContext != context);
    switch (context) {
      case ViewContext.Teams:
        this._customViews = this._customViews.concat(this.initialTeamSystemViews());
        break;
      case ViewContext.TeamRosters:
        this._customViews = this._customViews.concat(this.initialTeamRostersSystemViews());
        break;
      case ViewContext.TeamBench:
        this._customViews = this._customViews.concat(this.initialTeamBenchSystemViews());
        break;
      case ViewContext.People:
        this._customViews = this._customViews.concat(this.initialPeopleSystemViews());
        break;
      case ViewContext.TeamSurvey:
        this._customViews = this._customViews.concat(this.initialTeamSurveySystemViews());
        break;
      case ViewContext.Forms:
        this._customViews = this._customViews.concat(this.initialFormSystemViews());
        break;
      case ViewContext.OpportunityBoardReport:
        this._customViews = this._customViews.concat(this.initialOpportunityBoardSystemViews());
        break;
      case ViewContext.Evaluations:
        this._customViews = this._customViews.concat(this.initialEvaluationsSystemViews());
        break;
      case ViewContext.InProgressEntityCredentialApplications:
        this._customViews = this._customViews.concat(
          this.initialInProgressEntityCredentialApplicationsSystemViews()
        );
        break;
      case ViewContext.PersonCredentialApplicationReport:
        this._customViews = this._customViews.concat(
          this.initialPersonApplicationReportSystemViews()
        );
        break;
      case ViewContext.TeamCredentialApplicationReport:
        this._customViews = this._customViews.concat(
          this.initialTeamApplicationReportSystemViews()
        );
        break;
      case ViewContext.PersonScenarioReport:
        this._customViews = this._customViews.concat(this.initialPersonScenarioReportSystemViews());
        break;
      case ViewContext.TeamScenarioReport:
        this._customViews = this._customViews.concat(this.initialTeamScenarioReportSystemViews());
        break;
      case ViewContext.CredentialingDetailsReport || ViewContext.AllCredentialingDetailsReport:
        this._customViews = this._customViews.concat(
          this.initialCredentialingDetailsReportSystemViews()
        );
        break;
      case ViewContext.TeamCredentialingDetailsReport ||
        ViewContext.AllTeamCredentialingDetailsReport:
        this._customViews = this._customViews.concat(
          this.initialTeamCredentialingDetailsReportSystemViews()
        );
        break;
      case ViewContext.AllInvoiceReport:
        this._customViews = this._customViews.concat(this.initialAllInvoiceReportSystemViews());
        break;
      case ViewContext.AllPaymentRecordsReport || ViewContext.CredentialPaymentRecordsReport:
        this._customViews = this._customViews.concat(this.initialPaymentRecordsReportSystemViews());
        break;
      case ViewContext.TeamPageRosterDetail:
        this._customViews = this._customViews.concat(this.initialTeamPageRosterDetailSystemViews());
        break;
    }
    this.customViews.next(this._customViews);
  }

  protected initialTeamSystemViews(): CustomView[] {
    let activeTeamFilter = new SelectedFilter({
      filter: TEAM_SYSTEM_FILTER_MODELS.TeamStatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: 'Active'
    });

    let archivedTeamFilter = new SelectedFilter({
      filter: TEAM_SYSTEM_FILTER_MODELS.TeamStatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: 'Archived'
    });

    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.Teams,
          filters: [SavedFilter.parseToString(activeTeamFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Active Teams',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '22222222-2222-2222-2222-222222222222',
          viewContext: ViewContext.Teams,
          filters: [SavedFilter.parseToString(archivedTeamFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Archived Teams',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '33333333-3333-3333-3333-333333333333',
          viewContext: ViewContext.Teams,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Teams',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialTeamRostersSystemViews(): CustomView[] {
    let activeTeamRosterFilter = new SelectedFilter({
      filter: TEAM_ROSTERS_REPORT_FILTER_MODELS.TeamStatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: 'Active'
    });

    let thisYearTeamRosterFilter = new SelectedFilter({
      filter: TEAM_ROSTERS_REPORT_FILTER_MODELS.TeamStartDateFilterModel,
      filterOperator: OPERATORS.Date.operators.find(
        (op) => op.displayOperator == FilterOperatorType.thisYear
      )
    });

    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.TeamRosters,
          customViewDisplayLayout: CustomViewDisplayLayout.Table,
          filters: [SavedFilter.parseToString(activeTeamRosterFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Active Team Rosters',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '22222222-2222-2222-2222-222222222222',
          viewContext: ViewContext.TeamRosters,
          customViewDisplayLayout: CustomViewDisplayLayout.Table,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Team Rosters',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '33333333-3333-3333-3333-333333333333',
          viewContext: ViewContext.TeamRosters,
          customViewDisplayLayout: CustomViewDisplayLayout.Table,
          filters: [SavedFilter.parseToString(thisYearTeamRosterFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Team Rosters This Year',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialTeamBenchSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.TeamBench,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Interested',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialOpportunityBoardSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.OpportunityBoardReport,
          filters: [],
          quickFilterNames: [],
          isPublic: false,
          name: 'All',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialEvaluationsSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.Evaluations,
          filters: [],
          quickFilterNames: [],
          isPublic: false,
          name: 'All',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialPeopleSystemViews(): CustomView[] {
    let activePeopleFilter = new SelectedFilter({
      filter: PEOPLE_FILTER_MODELS.StatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: PersonStatus.Active
    });

    let potentialPeopleFilter = new SelectedFilter({
      filter: PEOPLE_FILTER_MODELS.StatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: PersonStatus.Potential
    });

    let archivedPeopleFilter = new SelectedFilter({
      filter: PEOPLE_FILTER_MODELS.StatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: PersonStatus.Archived
    });

    let noDeclinedPositionStatusFilter = new SelectedFilter({
      filter: PEOPLE_FILTER_MODELS.PositionStatusFilterModel,
      filterOperator: OPERATORS.MultiValue.operators.find(
        (op) => op.displayOperator == FilterOperatorType.in
      ),
      searchValues: [
        new ODataFilterValues(SeatStatus[SeatStatus.Accepted], SeatStatus[SeatStatus.Accepted]),
        new ODataFilterValues(SeatStatus[SeatStatus.Invited], SeatStatus[SeatStatus.Invited]),
        new ODataFilterValues(SeatStatus[SeatStatus.NoResponse], SeatStatus[SeatStatus.NoResponse]),
        new ODataFilterValues(SeatStatus[SeatStatus.Picked], SeatStatus[SeatStatus.Picked])
      ]
    });

    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.People,
          customViewDisplayLayout: CustomViewDisplayLayout.List,
          filters: [SavedFilter.parseToString(activePeopleFilter)],
          quickFilterNames: PEOPLE_DEFAULT_QUICK_FILTERS,
          isPublic: true,
          name: 'All Active People',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '22222222-2222-2222-2222-222222222222',
          viewContext: ViewContext.People,
          customViewDisplayLayout: CustomViewDisplayLayout.List,
          filters: [SavedFilter.parseToString(potentialPeopleFilter)],
          quickFilterNames: PEOPLE_DEFAULT_QUICK_FILTERS,
          isPublic: true,
          name: 'All Potential People',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '33333333-3333-3333-3333-333333333333',
          viewContext: ViewContext.People,
          customViewDisplayLayout: CustomViewDisplayLayout.List,
          filters: [SavedFilter.parseToString(archivedPeopleFilter)],
          quickFilterNames: PEOPLE_DEFAULT_QUICK_FILTERS,
          isPublic: true,
          name: 'All Archived People',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '44444444-4444-4444-4444-444444444444',
          viewContext: ViewContext.People,
          customViewDisplayLayout: CustomViewDisplayLayout.List,
          filters: [
            SavedFilter.parseToString(activePeopleFilter),
            SavedFilter.parseToString(noDeclinedPositionStatusFilter)
          ],
          quickFilterNames: PEOPLE_DEFAULT_QUICK_FILTERS,
          isPublic: true,
          name: 'All Active People on an Active Team',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialTeamSurveySystemViews(): CustomView[] {
    let completedFilter = new SelectedFilter({
      filter: TEAM_SURVEY_SYSTEM_FILTER_MODELS.SurveyStatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: SurveyStatus.Complete
    });

    let readyToCompleteFilter = new SelectedFilter({
      filter: TEAM_SURVEY_SYSTEM_FILTER_MODELS.SurveyStatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: SurveyStatus.ReadyToComplete
    });

    let lastMonthFilter = new SelectedFilter({
      filter: TEAM_SURVEY_SYSTEM_FILTER_MODELS.SurveySubmissionDateFilterModel,
      filterOperator: OPERATORS.Date.operators.find(
        (op) => op.displayOperator == FilterOperatorType.lastMonth
      )
    });

    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.TeamSurvey,
          customViewDisplayLayout: CustomViewDisplayLayout.Chart,
          filters: [SavedFilter.parseToString(completedFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'Completed Survey Responses',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '22222222-2222-2222-2222-222222222222',
          viewContext: ViewContext.TeamSurvey,
          customViewDisplayLayout: CustomViewDisplayLayout.Chart,
          filters: [SavedFilter.parseToString(readyToCompleteFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'Not Completed Survey Responses',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '33333333-3333-3333-3333-333333333333',
          customViewDisplayLayout: CustomViewDisplayLayout.Chart,
          viewContext: ViewContext.TeamSurvey,
          filters: [SavedFilter.parseToString(lastMonthFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'Last Month Survey Responses',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '44444444-4444-4444-4444-444444444444',
          viewContext: ViewContext.TeamSurvey,
          customViewDisplayLayout: CustomViewDisplayLayout.Chart,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Survey Responses',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialFormSystemViews(): CustomView[] {
    let submittedFilter = new SelectedFilter({
      filter: FORM_SYSTEM_FILTER_MODELS.SubmissionStatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: FormStatus.Submitted
    });

    let notSubmittedFilter = new SelectedFilter({
      filter: FORM_SYSTEM_FILTER_MODELS.SubmissionStatusFilterModel,
      filterOperator: OPERATORS.List.operators.find(
        (op) => op.displayOperator == FilterOperatorType.eq
      ),
      searchValue: FormStatus.NotSubmitted
    });

    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.Forms,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Submissions',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '22222222-2222-2222-2222-222222222222',
          viewContext: ViewContext.Forms,
          filters: [SavedFilter.parseToString(submittedFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'Submitted Forms',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '33333333-3333-3333-3333-333333333333',
          viewContext: ViewContext.Forms,
          filters: [SavedFilter.parseToString(notSubmittedFilter)],
          quickFilterNames: [],
          isPublic: true,
          name: 'Not Submitted Forms',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialPersonApplicationReportSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.PersonCredentialApplicationReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'Person Credential Applications',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialTeamApplicationReportSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.TeamCredentialApplicationReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'Team Credential Applications',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialInProgressEntityCredentialApplicationsSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.InProgressEntityCredentialApplications,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Applications',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialPersonScenarioReportSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.PersonScenarioReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Person Scenarios',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialTeamScenarioReportSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.TeamScenarioReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Team Scenarios',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialCredentialingDetailsReportSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.CredentialingDetailsReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Person Credentials',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.AllCredentialingDetailsReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Person Credentials',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialTeamCredentialingDetailsReportSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.TeamCredentialingDetailsReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Team Credentials',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.AllTeamCredentialingDetailsReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Team Credentials',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }
  protected initialAllInvoiceReportSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.AllInvoiceReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All invoice report',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialPaymentRecordsReportSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.AllPaymentRecordsReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'All Payment Records',
          auth0UserId: '',
          columns: []
        },
        true
      ),
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.CredentialPaymentRecordsReport,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'Credential Payment Records',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  protected initialTeamPageRosterDetailSystemViews(): CustomView[] {
    let systemViews: CustomView[] = [
      new CustomView(
        {
          id: '11111111-1111-1111-1111-111111111111',
          viewContext: ViewContext.TeamPageRosterDetail,
          filters: [],
          quickFilterNames: [],
          isPublic: true,
          name: 'Team Page Roster Detail',
          auth0UserId: '',
          columns: []
        },
        true
      )
    ];

    return systemViews;
  }

  private convertToCustomViews(data: { defaultViews: DefaultView[]; customViews: CustomView[] }) {
    let customViews: CustomView[] = Array<CustomView>().concat(
      this.initialTeamSystemViews(),
      this.initialTeamRostersSystemViews(),
      this.initialTeamBenchSystemViews(),
      this.initialPeopleSystemViews(),
      this.initialTeamSurveySystemViews(),
      this.initialEvaluationsSystemViews(),
      this.initialFormSystemViews(),
      this.initialInProgressEntityCredentialApplicationsSystemViews(),
      this.initialPersonScenarioReportSystemViews(),
      this.initialTeamScenarioReportSystemViews(),
      this.initialPersonApplicationReportSystemViews(),
      this.initialTeamApplicationReportSystemViews(),
      this.initialCredentialingDetailsReportSystemViews(),
      this.initialTeamCredentialingDetailsReportSystemViews(),
      this.initialPaymentRecordsReportSystemViews(),
      this.initialAllInvoiceReportSystemViews(),
      this.initialOpportunityBoardSystemViews(),
      this.initialTeamPageRosterDetailSystemViews()
    );
    if (data && data.customViews) {
      customViews = customViews.concat(
        data.customViews.map((cv) => {
          let customView = new CustomView(cv);
          if (customView.quickFilterNames.length == 0) {
            customView.quickFilterNames =
              customView.viewContext == ViewContext.People ? PEOPLE_DEFAULT_QUICK_FILTERS : [];
          }
          return customView;
        })
      );
    }
    customViews.forEach((customView) => {
      customView.isDefault =
        (data?.defaultViews ?? []).findIndex(
          (x) => x.defaultViewId == customView.id && x.viewContext == customView.viewContext
        ) != -1;
      if (this.viewContextHasSystemFieldVisibility(customView.viewContext)) {
        customView.quickFilterNames = filterQuickFilterNamesBySystemFieldVisibility(
          customView.quickFilterNames,
          this.appConfigService.tenant?.settings
        );
      }
    });
    return customViews;
  }

  private viewContextHasSystemFieldVisibility(viewContext: ViewContext): boolean {
    return (
      viewContext == ViewContext.People ||
      viewContext == ViewContext.TeamRosters ||
      viewContext == ViewContext.TeamBench ||
      viewContext == ViewContext.TeamPageRosterDetail
    );
  }
}
